import round from 'lodash/round'
import { MIN_SCENE_SECONDS } from './clipSceneTiming'
import type { TranscriptData, TranscriptUtterance } from './TranscriptData'

/**
 * Which parts of a transcript window a render plays (#89).
 *
 * One set of numbers for everyone: the editor's skip playback, the render
 * dialog's preview and `createProject` all derive their timeline from here, so
 * what the preview plays is what the render plays.
 *
 * ```
 *   window      [start ─────────────────────────────────────── end]
 *   skipped           ▓▓▓ (removed words)        ▓▓▓▓ (skipSegments)
 *   kept        [────)   (──────────────────────)    (─────────)
 * ```
 *
 * Speaker-filter gaps (#304) are deliberately not cuts: they are a playback
 * nicety in the editor, not an edit, so they stay out of here.
 */

export interface SkippedRun {
  uttI: number
  /** Index of the run's first word in the unfiltered `words`. */
  wordI: number
  wordIds: string[]
  text: string
  start: number
  end: number
}

export interface Range {
  start: number
  end: number
}

/** Runs closer together than this are one jump, not two. */
const MERGE_TOLERANCE = 0.01

/** Consecutive removed words, grouped per utterance (a run never crosses one). */
export function groupSkippedRuns(
  utterances: readonly TranscriptUtterance[],
): SkippedRun[] {
  const runs: SkippedRun[] = []

  utterances.forEach((utterance, uttI) => {
    let open: SkippedRun | null = null
    utterance.words.forEach((w, wordI) => {
      if (!w.removed) {
        open = null
        return
      }
      if (!open) {
        open = {
          uttI,
          wordI,
          wordIds: [],
          text: '',
          start: w.start,
          end: w.end,
        }
        runs.push(open)
      }
      open.wordIds.push(w.id)
      open.text = open.text ? `${open.text} ${w.text}` : w.text
      open.end = w.end
    })
  })

  return runs
}

/**
 * Sort, merge within `MERGE_TOLERANCE`, and drop zero-length ranges.
 *
 * `nextSkipJump` assumes its ranges are sorted and disjoint. Two sources feed
 * it — removed words and the speaker filter's gaps (#304) — so they go through
 * this one merge, and a removed word beside a speaker gap is one jump rather
 * than an audible blip between two.
 */
export function mergeRanges(ranges: readonly Range[]): Range[] {
  const sorted = ranges
    .filter((r) => r.end > r.start)
    .sort((a, b) => a.start - b.start)
  const merged: Range[] = []
  for (const range of sorted) {
    const last = merged[merged.length - 1]
    if (last && range.start <= last.end + MERGE_TOLERANCE) {
      last.end = Math.max(last.end, range.end)
    } else {
      merged.push({ start: range.start, end: range.end })
    }
  }
  return merged
}

/** Everything a render skips: runs of removed words plus `skipSegments`, merged. */
export function skipRangesOf(
  data: Pick<TranscriptData, 'utterances' | 'skipSegments'>,
): Range[] {
  return mergeRanges([
    ...groupSkippedRuns(data.utterances),
    ...(data.skipSegments ?? []),
  ])
}

/**
 * The window `[start, end]` minus the skip ranges — the recording-relative
 * pieces a render plays, in order.
 *
 * Pieces shorter than one frame are dropped (there is nothing to show of
 * them, and a zero-frame `<Series.Sequence>` is an error), and every bound is
 * rounded to milliseconds, the unit `resolveClipSceneTiming` works in.
 *
 * An empty result means everything in the window is skipped. Callers refuse
 * that rather than rendering it.
 */
export function keptRanges(
  data: Pick<TranscriptData, 'utterances' | 'skipSegments'>,
  start: number,
  end: number,
): Range[] {
  return keptRangesWithin(skipRangesOf(data), start, end)
}

/**
 * `keptRanges` over skip ranges already derived (`skipRangesOf`), sorted and
 * disjoint: what a project carries per recording, so a scene's pieces are
 * computed without the transcript (`sceneMap.ts`).
 */
export function keptRangesWithin(
  skips: readonly Range[],
  start: number,
  end: number,
): Range[] {
  const from = round(start, 3)
  const to = round(Math.max(start, end), 3)

  const kept: Range[] = []
  let cursor = from

  const push = (a: number, b: number) => {
    const s = round(a, 3)
    const e = round(b, 3)
    if (e - s >= MIN_SCENE_SECONDS) kept.push({ start: s, end: e })
  }

  for (const skip of skips) {
    if (skip.end <= cursor) continue
    if (skip.start >= to) break
    if (skip.start > cursor) push(cursor, skip.start)
    cursor = Math.max(cursor, skip.end)
    if (cursor >= to) break
  }
  if (cursor < to) push(cursor, to)

  return kept
}

/** Total length of `ranges`, in seconds, rounded to milliseconds. */
export function rangesDuration(ranges: readonly Range[]): number {
  return round(
    ranges.reduce((sum, r) => sum + (r.end - r.start), 0),
    3,
  )
}
